
// ================== Imports ==================
import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import ApiProvider from '../../../apiProvider';
import * as Icon from 'react-bootstrap-icons';
import { useMsal } from '@azure/msal-react';
import { CdsContentPanel } from 'clayco-component-library';
import { CdsDataTable } from 'clayco-component-library/lib';
import StayBenefitPolicyPanel from './components/StayBenefitPolicyPanel';
import PendingApprovalsList from './components/PendingApprovalsList';
import MySubmissionsList from './components/MySubmissionsList';
import StayBenefitModalForm from './components/StayBenefitModalForm';
import { useEmployeeNumber } from '../../../employeeNumber';
import { isSelfApproval, SELF_APPROVAL_ERROR_MSG, notifySelfApproval } from '../../../utility/selfApprovalCheck';
import { buildProjectOptionLabel } from '../../../utility/projectCompany';


const calculateDays = (start, end) => {
  if (!start || !end) return '';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  if (isNaN(s) || isNaN(e) || e < s) return '';
  return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
};


const StayBenefitForm = ({ setMenuOptions }) => {
  const [showModal, setShowModal] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  // Maintain projectAssignment as an empty array initially so yup array().min(1) validation works.
  const [formData, setFormData] = useState({ projectAssignment: [], startDate: '', endDate: '' });
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const activeAccount = msalAccounts?.[0];
  const { employeeNumber } = useEmployeeNumber();

  // Approval history state
  const historyGridColumns = ['Employee', 'Job', 'Submitted', 'Period', 'Days', 'Approver', 'Reviewed On', 'Is Approved?'];
  const historyDataKeys = ['employee', 'job', 'submitted', 'period', 'days', 'approver', 'approvedOn', 'isApproved'];
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Project fetch
  const fetchProjects = async () => {
    if (!msalInstance || !activeAccount) return;
    setProjectLoading(true);
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('project/clayco-sei');
      let options = [];
      if (Array.isArray(response)) {
        options = response.map(p => ({ label: buildProjectOptionLabel(p.name, p.number), value: p.number, ...p }));
      } else if (Array.isArray(response?.items)) {
        options = response.items.map(p => ({ label: buildProjectOptionLabel(p.name, p.number), value: p.number, ...p }));
      }
      options.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      setProjectOptions(options);
    } catch {
      setProjectOptions([]);
    } finally {
      setProjectLoading(false);
    }
  };

  // Eligibility logic for Save button
  const daysWithoutTravel = calculateDays(formData.startDate, formData.endDate);
  const minDaysMet = daysWithoutTravel !== '' && daysWithoutTravel >= 30;
  const endDateObj = formData.endDate ? new Date(formData.endDate + 'T00:00:00') : null;
  const eligibilityCutoff = new Date('2025-09-30T00:00:00');
  const endDateValid = endDateObj && endDateObj > eligibilityCutoff;
  const hasProjectSelection = Array.isArray(formData.projectAssignment)
    ? formData.projectAssignment.length > 0
    : !!formData.projectAssignment;
  const canSave = minDaysMet && endDateValid && hasProjectSelection;


  const stayBenefitSchema = yup.object().shape({
    projectAssignment: yup
      .array()
      .of(yup.object().shape({ value: yup.mixed().required() }))
      .min(1, 'Project Assignment is required')
      .required('Project Assignment is required'),
    startDate: yup.string().required('Start Date is required').test('valid-date', 'Start Date is invalid', val => !val || !isNaN(new Date(val).getTime())),
    endDate: yup.string().required('End Date is required')
      .test('valid-date', 'End Date is invalid', val => !val || !isNaN(new Date(val).getTime()))
      .test('after-start', 'End Date must be after Start Date', function (val) {
        const { startDate } = this.parent;
        return !val || !startDate || new Date(val).getTime() >= new Date(startDate).getTime();
      }),
  });


  const validateDuration = (start, end) => {
    const days = calculateDays(start, end);
    return days === '' ? null : days < 30 ? 'Non-travel period must be at least 30 consecutive days (inclusive).' : null;
  };


  const submitStayBenefit = async (requestBody) => {
    if (!msalInstance || !activeAccount) {
      setPeriods(prev => [...prev, { ...requestBody, daysWithoutTravel: calculateDays(requestBody.startDate, requestBody.endDate), localOnly: true }]);
      return { warning: 'MSAL instance/account missing; added locally only.' };
    }
    const api = new ApiProvider(msalInstance, activeAccount);
    // Use consistent lowercase endpoint
    const response = await api.Post('staybenefit', requestBody);
    if (response?.error) {
      const err = response.error;
      let msg = err.message || 'Error submitting Stay Benefit.';
      if (err.code === 409) msg = 'Submission overlaps with an existing pending/approved period.';
      if (err.details && Array.isArray(err.details)) {
        const mapped = {};
        err.details.forEach(d => { if (d.field) mapped[d.field] = d.issue || msg; });
        setFieldErrors(prev => ({ ...prev, ...mapped }));
      }
      alert(msg);
      return { error: msg };
    }
    return response;
  };


  const fetchMyStayBenefits = async () => {
    setIsLoading(true); setLoadError('');
    try {
      if (!msalInstance || !activeAccount) {
        setLoadError('Authentication required.');
        setPeriods([]);
        return;
      }
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('staybenefit/me');
      if (response?.error) {
        setLoadError(response.error.message || 'Failed to load stay benefit data.');
        setPeriods([]);
        return;
      }
      const items = Array.isArray(response?.items) ? response.items : [];
      setPeriods(
        items.map(item => ({
          id: item.id,
          employeeNumber: item.employeeNumber,
          firstName: item.firstName,
          lastName: item.lastName,
          projectName: item.projectName,
          projectNumber: item.projectNumber,
          startDate: item.startDate,
          endDate: item.endDate,
          daysWithoutTravel: calculateDays(item.startDate, item.endDate),
          status: item.isApproved === true ? 'approved' : item.isApproved === false ? 'rejected' : 'pending',
          approverName: item.approverName,
          approvedOn: item.approvedOn,
          isActive: item.isActive
        }))
      );
    } catch (err) {
      setLoadError('Failed to load stay benefit data.');
      setPeriods([]);
    } finally {
      setIsLoading(false);
    }
  };


  const putStayBenefit = async (requestBody) => {
    if (!msalInstance || !activeAccount) {
      alert('Authentication required.');
      return { error: 'No auth' };
    }
    const api = new ApiProvider(msalInstance, activeAccount);
    if (!requestBody?.id) {
      return { error: 'Missing id for update.' };
    }
    const response = await api.Put(`staybenefit/`, requestBody);
    if (response?.error) {
      const err = response.error;
      let msg = err.message || 'Error updating Stay Benefit.';
      if (err.code === 409) msg = 'Update overlaps with an existing pending/approved period.';
      if (err.details && Array.isArray(err.details)) {
        const mapped = {};
        err.details.forEach(d => { if (d.field) mapped[d.field] = d.issue || msg; });
        setFieldErrors(prev => ({ ...prev, ...mapped }));
      }
      alert(msg);
      return { error: msg };
    }
    return response;
  };

  const savePeriod = async (validData) => {
    // Prevent submission if no project selected (guards against blank API payload causing crash).
    if (!validData.projectAssignment || (Array.isArray(validData.projectAssignment) && validData.projectAssignment.length === 0)) {
      setFieldErrors(prev => ({ ...prev, projectAssignment: 'Project Assignment is required' }));
      return;
    }
    const durationError = validateDuration(validData.startDate, validData.endDate);
    if (durationError) { setFieldErrors(prev => ({ ...prev, endDate: durationError })); return; }
    setIsSubmitting(true);
    try {
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      // Extract the selected project. The typeahead returns an array; we only allow a single selection.
      const project = Array.isArray(validData.projectAssignment) ? validData.projectAssignment[0] : validData.projectAssignment;

      // Display label includes the project number e.g. "Project X (12345)" for user clarity,
      // but when persisting we must send only the raw project name and the project number separately.
      const projectNumber = project?.value || '';
      const projectName = project?.name || '';

      const req = {
        projectNumber,
        projectName,
        startDate: formatDate(validData.startDate),
        endDate: formatDate(validData.endDate),
        approverEmail: project?.concurApprover || '',
      };
      if (editMode && editId) {
        // Ensure id passed for PUT path param
        req.id = editId;
        const apiResult = await putStayBenefit(req);
        // Treat an undefined/empty response as success (common for 204 No Content)
        if (!apiResult?.error) {
          await Promise.all([fetchMyStayBenefits(), fetchReviewStayBenefits()]);
          closeModal();
        }
      } else {
        const apiResult = await submitStayBenefit(req);
        if (!apiResult?.error) {
          await Promise.all([fetchMyStayBenefits(), fetchReviewStayBenefits()]);
          closeModal();
        }
      }
    } finally { setIsSubmitting(false); }
  };


  const handleFormError = (errObj) => {
    if (!errObj) return;
    if (Array.isArray(errObj)) {
      const mapped = {};
      errObj.forEach(e => { if (e?.path) mapped[e.path] = e.message; });
      setFieldErrors(mapped);
    } else setFieldErrors(errObj);
  };
  const resetForm = () => {
    setFormData({ projectAssignment: [], startDate: '', endDate: '' });
    setFieldErrors({});
    setIsSubmitting(false);
    setIsDirty(false);
    setEditMode(false);
    setEditId(null);
  };
  const openModal = () => {
    resetForm();
    setShowModal(true);
  };
  const openEditModal = async (record) => {
    console.log('[StayBenefit] openEditModal start:', { record });
    // Ensure projects are loaded so initialValue in typeahead can match actual option object.
    if (!projectOptions || projectOptions.length === 0) {
      console.log('[StayBenefit] projectOptions empty before fetch, invoking fetchProjects');
      try { await fetchProjects(); } catch (e) { console.warn('[StayBenefit] fetchProjects failed', e); }
      console.log('[StayBenefit] projectOptions after fetch:', projectOptions.length);
    }

    // Normalize incoming record fields.
    const recNumber = String(record.projectNumber || record.projectAssignment || '').trim();
    const recName = String(record.projectName || '').trim();
    console.log('[StayBenefit] normalized record fields', { recNumber, recName });

    // Match by number first; fallback to case-insensitive name match.
    let existingOpt = projectOptions.find(opt => String(opt.value).trim() === recNumber);
    console.log('[StayBenefit] match by number result:', existingOpt);
    if (!existingOpt && recName) {
      existingOpt = projectOptions.find(opt => String(opt.name || opt.projectName || '').trim().toLowerCase() === recName.toLowerCase());
      console.log('[StayBenefit] match by name result:', existingOpt);
    }

    // If still not found create placeholder; reconciliation effect will update once options arrive (already implemented).
    if (!existingOpt && recNumber) {
      existingOpt = {
        label: buildProjectOptionLabel(recName, recNumber),
        value: recNumber,
        name: recName,
        number: recNumber,
        concurApprover: record.approverEmail || record.approverName || record.concurApprover || ''
      };
      console.log('[StayBenefit] created placeholder option:', existingOpt);
    }

    setFormData({
      projectAssignment: existingOpt ? [existingOpt] : [],
      startDate: record.startDate || '',
      endDate: record.endDate || '',
    });
    console.log('[StayBenefit] formData after openEditModal set:', { projectAssignment: existingOpt, startDate: record.startDate, endDate: record.endDate });
    setEditMode(true);
    setEditId(record.id);
    setFieldErrors({});
    setIsSubmitting(false);
    setIsDirty(false);
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveMessage, setApproveMessage] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [approvingLoading, setApprovingLoading] = useState(false);

  const openApproveModal = id => {
    setApprovingId(id);
    setApproveMessage('');
    setShowApproveModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!msalInstance || !activeAccount) return alert('Authentication required.');
    // Self-approval guard
    const target = pendingApprovals.find(item => item.id === approvingId);
    if (target && isSelfApproval(employeeNumber?.number, target.employeeNumber)) {
      const api = new ApiProvider(msalInstance, activeAccount);
      notifySelfApproval(api, { employeeNumber: target.employeeNumber, employeeName: `${target.firstName || ''} ${target.lastName || ''}`.trim(), submissionId: target.id, benefitType: 'StayBenefit' });
      alert(SELF_APPROVAL_ERROR_MSG);
      return;
    }
    try {
      setApprovingLoading(true);
      const api = new ApiProvider(msalInstance, activeAccount);
      await api.Post(`staybenefit/${approvingId}/approve`, { Message: approveMessage, Notify: true });
      setPendingApprovals(prev => prev.filter(item => item.id !== approvingId));
      setShowApproveModal(false);
      setApprovingId(null);
      setApproveMessage('');
      fetchReviewStayBenefits();
      fetchMyStayBenefits();
    } catch (err) {
      alert('Failed to approve record.');
    } finally {
      setApprovingLoading(false);
    }
  };
  const [rejectingLoading, setRejectingLoading] = useState(false);

  const openRejectModal = id => {
    setRejectingId(id);
    setRejectMessage('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!msalInstance || !activeAccount) return alert('Authentication required.');
    // Self-approval guard (also blocks self-rejection)
    const rejectTarget = pendingApprovals.find(item => item.id === rejectingId);
    if (rejectTarget && isSelfApproval(employeeNumber?.number, rejectTarget.employeeNumber)) {
      const api = new ApiProvider(msalInstance, activeAccount);
      notifySelfApproval(api, { employeeNumber: rejectTarget.employeeNumber, employeeName: `${rejectTarget.firstName || ''} ${rejectTarget.lastName || ''}`.trim(), submissionId: rejectTarget.id, benefitType: 'StayBenefit' });
      alert(SELF_APPROVAL_ERROR_MSG);
      return;
    }
    // Enforce rejection message requirement (UI already disables button, but double-guard here).
    if (!rejectMessage || !rejectMessage.trim()) {
      alert('Rejection message is required before rejecting the submission.');
      return;
    }
    try {
      setRejectingLoading(true);
      const api = new ApiProvider(msalInstance, activeAccount);
      await api.Post(`staybenefit/${rejectingId}/deny`, { Message: rejectMessage, Notify: true });
      setPendingApprovals(prev => prev.filter(item => item.id !== rejectingId));
      setShowRejectModal(false);
      setRejectingId(null);
      setRejectMessage('');
      fetchReviewStayBenefits();
      fetchMyStayBenefits();
    } catch (err) {
      alert('Failed to reject record.');
    } finally {
      setRejectingLoading(false);
    }
  };


  const fetchReviewStayBenefits = async () => {
    if (!msalInstance || !activeAccount) {
      setPendingApprovals([]);
      return;
    }
    const api = new ApiProvider(msalInstance, activeAccount);
    const response = await api.Get('staybenefit/review');
    if (response?.error) {
      setPendingApprovals([]);
      return;
    }
    const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [response];
    setPendingApprovals(
      items.map(item => ({
        id: item.id,
        employeeNumber: item.employeeNumber,
        firstName: item.firstName,
        lastName: item.lastName,
        projectName: item.projectName,
        projectNumber: item.projectNumber,
        startDate: item.startDate,
        endDate: item.endDate,
        totalDays: calculateDays(item.startDate, item.endDate),
        status: item.isApproved === true ? 'approved' : item.isApproved === false ? 'rejected' : 'pending',
        approverName: item.approverName,
        approvedOn: item.approvedOn,
        isActive: item.isActive,
        submittedOn: item.submittedOn,
        isApproved: item.isApproved
      }))
    );
  };

  useEffect(() => { if (typeof setMenuOptions === 'function') setMenuOptions('StayBenefitForm'); }, []);
  useEffect(() => {
    if (msalInstance && activeAccount) {
      fetchMyStayBenefits();
      fetchReviewStayBenefits();
      fetchProjects();
      fetchApprovalHistory();
    }
  }, [msalInstance, activeAccount]);

  // Reconcile placeholder project option after real options load in edit mode.
  useEffect(() => {
    if (!editMode) return;
    if (!Array.isArray(formData.projectAssignment) || formData.projectAssignment.length === 0) return;
    const current = formData.projectAssignment[0];
    if (!current || !current.value) return;
    console.log('[StayBenefit] reconcile effect current value:', current.value, 'options length:', projectOptions.length);
    const matched = projectOptions.find(opt => String(opt.value) === String(current.value));
    if (matched && matched !== current) {
      console.log('[StayBenefit] replacing placeholder with real option:', matched);
      setFormData(prev => ({ ...prev, projectAssignment: [matched] }));
    } else {
      console.log('[StayBenefit] no replacement needed (matched same or not found).');
    }
  }, [projectOptions, editMode, formData.projectAssignment]);

  // =====================
  // Approval History Helpers & Fetch
  // =====================
  const toMMDDYYYY = (dt) => {
    if (!dt || dt === '0001-01-01T00:00:00') return '';
    const cleaned = String(dt).replace(/"+$/, '');
    const match = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
  };

  const formatHistoryRow = (item) => ({
    employee: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
    job: `${item.projectName || ''}${item.projectNumber ? ' (' + item.projectNumber + ')' : ''}`.trim(),
    submitted: toMMDDYYYY(item.submittedOn),
    period: `${toMMDDYYYY(item.startDate)} – ${toMMDDYYYY(item.endDate)}`,
    days: item.startDate && item.endDate ? (Math.round((new Date(item.endDate) - new Date(item.startDate)) / (1000*60*60*24)) + 1) : '',
    approver: item.approverName || '',
    approvedOn: toMMDDYYYY(item.approvedOn),
    isApproved: item.isApproved === true ? 'Yes' : item.isApproved === false ? 'No' : ''
  });

  const fetchApprovalHistory = async () => {
    if (!msalInstance || !activeAccount) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('StayBenefit/history');
      const items = Array.isArray(response?.items) ? response.items : [];
      const sorted = items.slice().sort((a, b) => new Date(b.approvedOn) - new Date(a.approvedOn));
      setHistoryItems(sorted);
      setHistoryPage(1);
    } catch {
      setHistoryItems([]);
      setHistoryError('Failed to load approval history.');
    } finally {
      setHistoryLoading(false);
    }
  };


  return (
    <div style={{ padding: '1.5rem' }}>
      <StayBenefitPolicyPanel
        successMessage={successMessage}
        isLoading={isLoading}
        loadError={loadError}
        onAdd={openModal}
      >
        <PendingApprovalsList
          pendingApprovals={pendingApprovals}
          currentEmployeeNumber={employeeNumber?.number}
          openApproveModal={openApproveModal}
          openRejectModal={openRejectModal}
          showApproveModal={showApproveModal}
          setShowApproveModal={setShowApproveModal}
          approveMessage={approveMessage}
          setApproveMessage={setApproveMessage}
          handleApproveSubmit={handleApproveSubmit}
          approvingLoading={approvingLoading}
          showRejectModal={showRejectModal}
          setShowRejectModal={setShowRejectModal}
          rejectMessage={rejectMessage}
          setRejectMessage={setRejectMessage}
          handleRejectSubmit={handleRejectSubmit}
          rejectingLoading={rejectingLoading}
        />
        <MySubmissionsList
          periods={periods}
          openEditModal={openEditModal}
          handleDelete={async (p) => {
            if (!window.confirm('Are you sure you want to delete this record?')) return;
            if (!msalInstance || !activeAccount) return alert('Authentication required.');
            try {
              const api = new ApiProvider(msalInstance, activeAccount);
              await api.Delete(`staybenefit/${p.id}`);
              setPeriods(prev => prev.filter(period => period.id !== p.id));
              fetchMyStayBenefits();
            } catch {}
          }}
        />
      </StayBenefitPolicyPanel>
      <StayBenefitModalForm
        showModal={showModal}
        closeModal={closeModal}
        editMode={editMode}
        isSubmitting={isSubmitting}
        canSave={canSave}
        savePeriod={savePeriod}
        stayBenefitSchema={stayBenefitSchema}
        handleFormError={handleFormError}
        formData={formData}
        setFormData={setFormData}
        setIsDirty={setIsDirty}
        fieldErrors={fieldErrors}
        projectOptions={projectOptions}
        projectLoading={projectLoading}
        calculateDays={calculateDays}
      />

      {/* =====================
          Approval History (visible to all users)
      ===================== */}
      {(() => {
        const historyPageCount = Math.max(1, Math.ceil(historyItems.length / historyPageSize));
        const historyGridItems = historyItems
          .slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize)
          .map(formatHistoryRow);
        return (
          <div style={{ marginTop: '1.5rem' }}>
            <CdsContentPanel title="Stay Benefit Approval History" collapsible={false}>
              {historyLoading && <div>Loading approval history...</div>}
              {historyError && <div className="text-danger">{historyError}</div>}
              {!historyLoading && !historyError && historyItems.length === 0 && <div>No approval history found.</div>}

              {!historyLoading && !historyError && historyItems.length > 0 && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(1)}>First</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(historyPage - 1)}>Prev</button>
                    <span>Page {historyPage} of {historyPageCount}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPage + 1)}>Next</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPageCount)}>Last</button>
                  </div>

                  <CdsDataTable columns={historyGridColumns} dataKeys={historyDataKeys} data={historyGridItems} usePaging={false} darkMode={true} pagingControlMode={"none"} />

                  <div className="d-flex align-items-center gap-2 mt-2">
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(1)}>First</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(historyPage - 1)}>Prev</button>
                    <span>Page {historyPage} of {historyPageCount}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPage + 1)}>Next</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPageCount)}>Last</button>
                  </div>
                </>
              )}
            </CdsContentPanel>
          </div>
        );
      })()}
    </div>
  );
};

export default StayBenefitForm;